import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";

import { ArrowLeftSVG, ArrowRightSVG, Button } from "@newsela/angelou";
import { debounce } from "@s-libs/micro-dash";

import {
  containerCss,
  homepageArticlesListCss,
  leftArrow,
  rightArrow,
  iconCss,
} from "./HorizontalScrollContainer.style";

interface IHorizontalScrollContainer {
  children: ReactNode;
}

const HorizontalScrollContainer = ({
  children,
}: IHorizontalScrollContainer) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [marginSize, setMarginSize] = useState("-1.5rem");

  const updateMarginSize = () => {
    setMarginSize(window.innerWidth < 568 ? "-1rem" : "-1.5rem");
  };

  const checkScrollButtons = () => {
    const el = listRef.current;
    if (el) {
      const amountOfExcessSideMargin = 24; // 1.5rem
      const isScrollable = el.scrollWidth > el.clientWidth;
      const canScrollLeft = el.scrollLeft > 0;
      const canScrollRight =
        Math.ceil(el.scrollLeft + el.clientWidth) <
        el.scrollWidth - amountOfExcessSideMargin;

      setCanScrollLeft(isScrollable && canScrollLeft);
      setCanScrollRight(isScrollable && canScrollRight);
    }
  };

  const scroll = (direction: "left" | "right") => {
    const el = listRef.current;
    if (el) {
      const scrollAmount =
        direction === "left"
          ? -Math.ceil(el.offsetWidth / 2)
          : Math.ceil(el.offsetWidth / 2);
      el.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "ArrowLeft":
        scroll("left");
        break;
      case "ArrowRight":
        scroll("right");
        break;
      default:
        break;
    }
  };

  const throttledCheckScrollButtons = debounce(checkScrollButtons, 2000);

  useEffect(() => {
    const el = listRef.current;
    if (el) {
      checkScrollButtons();
      el.addEventListener("scroll", throttledCheckScrollButtons);
    }

    return () => {
      if (el) {
        el.removeEventListener("scroll", throttledCheckScrollButtons);
      }
    };
  }, [children]);

  useEffect(() => {
    updateMarginSize();
    window.addEventListener("resize", updateMarginSize);

    return () => window.removeEventListener("resize", updateMarginSize);
  }, []);

  return (
    <div
      css={containerCss}
      style={{
        marginLeft: canScrollLeft ? marginSize : 0,
        marginRight: canScrollRight ? marginSize : 0,
      }}
    >
      {canScrollLeft && (
        <Button
          icon={{
            SvgComponent: ArrowLeftSVG,
          }}
          __cssFor={{ root: leftArrow, Icon: { root: iconCss } }}
          onClick={() => scroll("left")}
          ariaProps={{ "aria-label": "Scroll left" }}
          dataProps={{
            "data-qa-selector": "homepage-row-scroll-left-button",
          }}
        />
      )}
      <div
        css={homepageArticlesListCss}
        ref={listRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        role="region"
        aria-labelledby="scrollable-region-label"
        onScroll={checkScrollButtons}
      >
        <span id="scrollable-region-label" hidden>
          Scrollable content
        </span>
        {children}
      </div>
      {canScrollRight && (
        <Button
          icon={{
            SvgComponent: ArrowRightSVG,
          }}
          __cssFor={{ root: rightArrow, Icon: { root: iconCss } }}
          onClick={() => scroll("right")}
          ariaProps={{ "aria-label": "Scroll right" }}
          dataProps={{
            "data-qa-selector": "homepage-row-scroll-right-button",
          }}
        />
      )}
    </div>
  );
};

HorizontalScrollContainer.displayName = "HorizontalScrollContainer";
export default HorizontalScrollContainer;
